import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useAppSettings } from 'providers/contexts/SettingsContext';
import { useDataProvider } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import Label from 'components/Typography/Label';
import {
  DragModal,
  DragModalHeader,
  type CustomCloseReasons,
  type PossibleCloseEvent,
} from 'components/DragModal/DragModal';
import type { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { formatDate } from 'utility/functions';
import { ISO_8601 } from 'utility/constants';
import { bloxValues } from 'themes/constants';
import type { AssetQuickLaunchMode, AssetQuickLaunchPrefill } from './types';
import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { SingleDateDropdown } from 'components/Inputs/Dropdown/SingleDateDropdownInput/SingleDateDropdownInput';
import { TextDropdownInput } from 'components/Inputs/Dropdown/TextDropdownInput';
import {
  InputChoice,
  getFormControlSxShowLabel,
  getSelectSxShowLabel,
} from 'components/Inputs/Dropdown/dropdownConsts';
import { IconHelper, type IconTypes } from 'components/generic/IconHelper';
import {
  checkChangeWorkflowPermission,
  checkSiteAdminPermission,
} from 'utility/permissions';
import { Workflow } from 'providers/contexts/extensions/Workflows';
import { useStoryTemplates } from './useStoryTemplates';
import {
  normalizeWorkflowId,
  useValidatedDefaultWorkflowId,
} from 'utility/workflowUtils';

// Extend dayjs once for this module.
dayjs.extend(utc);
dayjs.extend(timezone);

// Custom sx overrides to remove placeholder offset padding
const getFormControlSxNoPadding = (theme: Theme): SystemStyleObject => ({
  ...getFormControlSxShowLabel(theme),
});

// Remove the padding from the placeholder text.
const getSelectSxNoPadding = (theme: Theme): SystemStyleObject => ({
  ...getSelectSxShowLabel(theme),
  pt: 0,
  '& .MuiSelect-select p': {
    py: 0.6,
  },
});

type AssetQuickLaunchModalProps = {
  open: boolean;
  assetLabel: string;
  assetType: string;
  actionLabel?: string;
  currentTitle?: string;
  defaultWorkflowId?: string | null;
  defaultStartTime?: string | null;
  defaultSlug?: string;
  onClose: () => void;
  onSubmit: (values: AssetQuickLaunchPrefill) => void;
  mode?: AssetQuickLaunchMode;
  prefill?: AssetQuickLaunchPrefill;
};

type FormState = {
  workflowId: string | null;
  startTime: string | null;
  slug: string;
  title: string;
  storyTemplateId: string | null;
};

type FormTouchedState = {
  workflowId: boolean;
  startTime: boolean;
  slug: boolean;
  title: boolean;
  storyTemplateId: boolean;
};

export const AssetQuickLaunchModal = ({
  open,
  assetLabel,
  assetType,
  actionLabel,
  currentTitle = '',
  defaultWorkflowId,
  defaultStartTime,
  defaultSlug,
  onClose,
  onSubmit,
  mode = 'create',
  prefill,
}: AssetQuickLaunchModalProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const { queryClient } = useCosmosQueryClient();
  const { sessionConfig } = useCosmos();
  const siteTimeZone = sessionConfig?.time_zone || 'UTC';

  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userCanChangeWorkflow, name: workflowPermName } =
    checkChangeWorkflowPermission(sessionConfig);

  const appSettings = useAppSettings().appSettings;
  const siteUserPrefs = appSettings?.site?.user;
  const globalUserPrefs = appSettings?.global?.user;

  // Story templates hook - only enabled for 'create' mode
  const isCreateMode = mode === 'create';
  const {
    templates: storyTemplates,
    isLoading: storyTemplatesLoading,
    isError: storyTemplatesError,
    fetchOnOpen: fetchStoryTemplatesOnOpen,
  } = useStoryTemplates({
    assetType,
    enabled: isCreateMode,
  });

  const [formState, setFormState] = useState<FormState>({
    workflowId: null,
    startTime: null,
    slug: '',
    title: currentTitle,
    storyTemplateId: null,
  });
  const [touchedState, setTouchedState] = useState<FormTouchedState>({
    workflowId: false,
    startTime: false,
    slug: false,
    title: false,
    storyTemplateId: false,
  });

  const assetIconType = useMemo<IconTypes>(() => {
    const normalizedType = assetType.toLowerCase();

    if (normalizedType.includes('image') || normalizedType.includes('photo')) {
      return 'image';
    }

    if (normalizedType.includes('video')) {
      return 'video';
    }

    if (normalizedType.includes('collection')) {
      return 'collection';
    }

    if (normalizedType.includes('poll')) {
      return 'poll';
    }

    if (normalizedType.includes('audio')) {
      return 'audio';
    }

    if (normalizedType.includes('file')) {
      return 'file';
    }

    if (normalizedType.includes('html')) {
      return 'html';
    }

    if (normalizedType.includes('link')) {
      return 'link';
    }

    if (normalizedType.includes('table')) {
      return 'table';
    }

    if (
      normalizedType.includes('article') ||
      normalizedType.includes('story') ||
      normalizedType.includes('post')
    ) {
      return 'article';
    }

    return 'template';
  }, [assetType]);

  const assetIcon = useMemo(() => {
    return (
      <IconHelper
        iconType={assetIconType}
        svgIconProps={{ sx: { fontSize: 24 } }}
      />
    );
  }, [assetIconType]);

  const workflowsQuery = useQuery({
    queryKey: ['assetQuickLaunchWorkflows'],
    queryFn: async () => {
      const result = await dataProvider.getWorkflows({});
      return result?.data ?? [];
    },
    retry: false,
  });

  const modalTitleText = t(
    mode === 'duplicate'
      ? 'blox.editorial.assetQuickLaunch.duplicateTitle'
      : mode === 'create-template'
        ? 'blox.editorial.assetQuickLaunch.createTemplateTitle'
        : mode === 'template'
          ? 'blox.editorial.assetQuickLaunch.templateTitle'
          : mode === 'use-template'
            ? 'blox.editorial.assetQuickLaunch.useTemplate.title'
            : 'blox.editorial.assetQuickLaunch.createTitle',
    {
      label: assetLabel,
    },
  );
  const submitButtonLabel = t(
    mode === 'duplicate'
      ? 'blox.editorial.assetQuickLaunch.duplicateAction'
      : mode === 'template' || mode === 'create-template'
        ? 'blox.editorial.assetQuickLaunch.templateAction'
        : mode === 'use-template'
          ? 'blox.editorial.assetQuickLaunch.useTemplate.action'
          : 'blox.editorial.assetQuickLaunch.createAction',
    {
      label: actionLabel || assetLabel,
    },
  );
  const cancelLabel = t('blox.actions.cancel');
  const workflowLabel = t('blox.editorial.assetQuickLaunch.workflow.label');
  const workflowHelper = t('blox.editorial.assetQuickLaunch.workflow.helper');
  const workflowUntitledLabel = t('blox.editorial.assetQuickLaunch.workflow.untitled');
  const workflowPermissionHelper = t('blox.permissions.msg.required_short', {
    name: workflowPermName,
  });
  const startTimeLabel = t('blox.editorial.assetQuickLaunch.startTime.label');
  const formattedTimeZone = siteTimeZone.replace(/_/g, ' ');
  const startTimeHelper = t('blox.editorial.assetQuickLaunch.startTime.helper', {
    timeZone: formattedTimeZone,
  });
  const slugLabel = t('blox.editorial.assetQuickLaunch.slug.label');
  const slugHelper = t('blox.editorial.assetQuickLaunch.slug.helper');
  const titleLabel =
    mode === 'template' || mode === 'create-template'
      ? t('blox.editorial.assetQuickLaunch.template.nameLabel')
      : t('blox.editorial.asset.title.label');
  const workflowsErrorMessage = t('blox.editorial.assetQuickLaunch.workflows.error');

  // Story template labels
  const storyTemplateLabel = t('blox.editorial.assetQuickLaunch.storyTemplate.label');
  const storyTemplateHelper = t('blox.editorial.assetQuickLaunch.storyTemplate.helper');
  const storyTemplateBlankLabel = t(
    'blox.editorial.assetQuickLaunch.storyTemplate.blank',
  );
  const storyTemplatesErrorMessage = t(
    'blox.editorial.assetQuickLaunch.storyTemplate.error',
  );

  // Helper flag to identify modes that involve template operations
  const isTemplateMode =
    mode === 'use-template' || mode === 'template' || mode === 'create-template';
  const shouldUseDefaultWorkflow =
    mode === 'create' || isTemplateMode || mode === 'duplicate';
  // Modes that show the editable template-name field
  const showTemplateNameField = mode === 'template' || mode === 'create-template';

  const defaultWorkflowCandidateId = useMemo(() => {
    if (defaultWorkflowId !== undefined) {
      return normalizeWorkflowId(defaultWorkflowId);
    }
    return normalizeWorkflowId(siteUserPrefs?.default_workflow);
  }, [defaultWorkflowId, siteUserPrefs?.default_workflow]);

  const {
    effectiveWorkflowId: effectiveDefaultWorkflowId,
    isPending: isDefaultWorkflowValidationPending,
    isInvalidCandidate: isDefaultWorkflowCandidateInvalid,
  } = useValidatedDefaultWorkflowId(defaultWorkflowCandidateId);

  const effectiveDefaultStartTime = useMemo(() => {
    if (defaultStartTime !== undefined) {
      return defaultStartTime;
    }
    const preference =
      siteUserPrefs?.default_start_time ?? globalUserPrefs?.default_start_time;
    const isValid = preference ? dayjs(preference).isValid() : false;
    return isValid ? preference || null : null;
  }, [
    defaultStartTime,
    siteUserPrefs?.default_start_time,
    globalUserPrefs?.default_start_time,
  ]);

  type DerivePrefillOptions = {
    includeSlug?: boolean;
    includeTitle?: boolean;
    overrides?: AssetQuickLaunchPrefill;
    useDefaults?: boolean;
  };

  // Helper to derive form state with consistent defaults, optionally including slug/title from props
  const derivePrefill = useCallback(
    (options?: DerivePrefillOptions): FormState => {
      const useDefaults = options?.useDefaults ?? true;
      const overrides = options?.overrides;

      return {
        workflowId:
          overrides?.workflowId !== undefined
            ? overrides.workflowId
            : useDefaults
              ? effectiveDefaultWorkflowId
              : null,
        startTime:
          overrides?.startTime !== undefined
            ? overrides.startTime
            : useDefaults
              ? effectiveDefaultStartTime
              : null,
        slug:
          overrides?.slug !== undefined
            ? (overrides.slug ?? '')
            : useDefaults && options?.includeSlug
              ? (defaultSlug ?? '')
              : '',
        title:
          overrides?.title !== undefined
            ? overrides.title
            : useDefaults && options?.includeTitle
              ? (currentTitle ?? '')
              : '',
        storyTemplateId:
          overrides?.storyTemplateId !== undefined ? overrides.storyTemplateId : null,
      };
    },
    [currentTitle, defaultSlug, effectiveDefaultStartTime, effectiveDefaultWorkflowId],
  );

  const initialFormState = useCallback((): FormState => {
    if (mode === 'create') {
      return derivePrefill(); // No slug/title prefill for new assets
    }

    if (isTemplateMode) {
      return derivePrefill({ includeSlug: true, includeTitle: true }); // Prefill slug and title for template operations
    }

    if (mode === 'duplicate') {
      // Duplicate mode: use user preference workflow, clear start time/slug.
      // Do NOT copy original asset's workflow, start time, or slug.
      return derivePrefill({
        overrides: {
          workflowId: effectiveDefaultWorkflowId ?? null,
          startTime: null,
          slug: '',
          title: '',
        },
        useDefaults: false,
      });
    }

    return derivePrefill({
      overrides: prefill,
      useDefaults: false,
    });
  }, [derivePrefill, effectiveDefaultWorkflowId, isTemplateMode, mode, prefill]);

  // Helper to derive touched state with consistent defaults, optionally marking slug/title as touched
  const deriveTouchedState = useCallback(
    (options?: { includeSlug?: boolean; includeTitle?: boolean }): FormTouchedState => {
      return {
        workflowId: Boolean(effectiveDefaultWorkflowId),
        startTime: Boolean(effectiveDefaultStartTime),
        slug: options?.includeSlug ? Boolean(defaultSlug) : false,
        title: options?.includeTitle ? Boolean(currentTitle) : false,
        storyTemplateId: false,
      };
    },
    [currentTitle, defaultSlug, effectiveDefaultStartTime, effectiveDefaultWorkflowId],
  );

  const initialTouchedState = useCallback((): FormTouchedState => {
    const hasPrefillWorkflow = prefill?.workflowId !== undefined;
    const hasPrefillStart = prefill?.startTime !== undefined;
    const hasPrefillSlug =
      typeof prefill?.slug === 'string' && prefill.slug.trim().length > 0;
    const hasPrefillTitle =
      typeof prefill?.title === 'string' && prefill.title.trim().length > 0;

    if (mode === 'create') {
      return deriveTouchedState(); // Mark workflow/start time as touched if defaults exist
    }

    if (isTemplateMode) {
      return deriveTouchedState({ includeSlug: true, includeTitle: true }); // Mark all fields as touched for template operations
    }

    if (mode === 'duplicate') {
      // Duplicate mode: only workflow can be pre-selected (from user defaults), start time/slug always empty.
      return {
        workflowId: Boolean(effectiveDefaultWorkflowId),
        startTime: false,
        slug: false,
        title: false,
        storyTemplateId: false,
      };
    }

    return {
      workflowId: hasPrefillWorkflow,
      startTime: hasPrefillStart,
      slug: hasPrefillSlug,
      title: hasPrefillTitle,
      storyTemplateId: false,
    };
  }, [
    deriveTouchedState,
    effectiveDefaultWorkflowId,
    isTemplateMode,
    mode,
    prefill?.slug,
    prefill?.startTime,
    prefill?.title,
    prefill?.workflowId,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormState(initialFormState());
    setTouchedState(initialTouchedState());

    // Fetch story templates when modal opens in create mode
    if (isCreateMode) {
      fetchStoryTemplatesOnOpen();
    }

    // Revalidate the default workflow candidate on every open so deleted
    // workflows are cleared without requiring a full page refresh.
    if (defaultWorkflowCandidateId) {
      queryClient.invalidateQueries({
        queryKey: ['validatedDefaultWorkflowById', defaultWorkflowCandidateId],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !shouldUseDefaultWorkflow) {
      return;
    }

    if (!effectiveDefaultWorkflowId) {
      return;
    }

    if (formState.workflowId || touchedState.workflowId) {
      return;
    }

    // If workflows are loaded and the default is missing, do not re-apply it.
    const defaultExistsInLoadedChoices = (workflowsQuery.data ?? []).some(
      (workflow) => workflow?.id === effectiveDefaultWorkflowId,
    );
    if (!workflowsQuery.isPending && !defaultExistsInLoadedChoices) {
      return;
    }

    setFormState((prev) => ({
      ...prev,
      workflowId: effectiveDefaultWorkflowId,
    }));
    setTouchedState((prev) => ({
      ...prev,
      workflowId: true,
    }));
  }, [
    effectiveDefaultWorkflowId,
    formState.workflowId,
    open,
    shouldUseDefaultWorkflow,
    touchedState.workflowId,
    workflowsQuery.data,
    workflowsQuery.isPending,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!defaultWorkflowCandidateId) {
      return;
    }

    if (isDefaultWorkflowValidationPending) {
      return;
    }

    if (!isDefaultWorkflowCandidateInvalid) {
      return;
    }

    setFormState((prev) => {
      if (prev.workflowId !== defaultWorkflowCandidateId) {
        return prev;
      }

      return {
        ...prev,
        workflowId: null,
      };
    });

    setTouchedState((prev) => {
      if (!prev.workflowId) {
        return prev;
      }

      return {
        ...prev,
        workflowId: false,
      };
    });
  }, [
    defaultWorkflowCandidateId,
    isDefaultWorkflowCandidateInvalid,
    isDefaultWorkflowValidationPending,
    open,
  ]);

  const workflowChoices: Workflow[] = useMemo(
    () => workflowsQuery.data ?? [],
    [workflowsQuery.data],
  );

  const workflowDropdownChoices = useMemo<InputChoice[]>(() => {
    const unique = new Map<string, InputChoice>();

    workflowChoices.forEach((workflow) => {
      if (!workflow?.id) {
        return;
      }

      const valueKey = workflow.id;
      if (unique.has(valueKey)) {
        return;
      }

      unique.set(valueKey, {
        label: workflow?.name || workflowUntitledLabel,
        value: workflow.id,
      });
    });

    return Array.from(unique.values());
  }, [workflowChoices, workflowUntitledLabel]);

  useEffect(() => {
    if (!open || workflowsQuery.isPending) {
      return;
    }

    const selectedWorkflowId = formState.workflowId;
    if (!selectedWorkflowId) {
      return;
    }

    const workflowStillExists = workflowDropdownChoices.some(
      (choice) => choice.value === selectedWorkflowId,
    );

    if (workflowStillExists) {
      return;
    }

    setFormState((prev) => ({
      ...prev,
      workflowId: null,
    }));
    setTouchedState((prev) => ({
      ...prev,
      workflowId: false,
    }));
  }, [formState.workflowId, open, workflowDropdownChoices, workflowsQuery.isPending]);

  // Story template dropdown choices - includes "Blank" as default option
  const storyTemplateDropdownChoices = useMemo<InputChoice[]>(() => {
    const choices: InputChoice[] = [
      {
        label: storyTemplateBlankLabel,
        value: '',
      },
    ];

    storyTemplates.forEach((template) => {
      if (!template?.uuid) {
        return;
      }

      choices.push({
        label: template.title || 'Untitled Template',
        value: template.uuid,
      });
    });

    return choices;
  }, [storyTemplates, storyTemplateBlankLabel]);

  const handleWorkflowSelect = useCallback(
    (_event: unknown, value: string | string[]) => {
      if (!userIsAdmin && !userCanChangeWorkflow) {
        return;
      }

      const nextValue = Array.isArray(value) ? value[0] : value;
      setFormState((prev) => ({
        ...prev,
        workflowId: nextValue ? nextValue : null,
      }));
      setTouchedState((prev) => ({
        ...prev,
        workflowId: true,
      }));
    },
    [userCanChangeWorkflow, userIsAdmin],
  );

  const handleWorkflowClear = useCallback(() => {
    if (!userIsAdmin && !userCanChangeWorkflow) {
      return;
    }

    setFormState((prev) => ({
      ...prev,
      workflowId: null,
    }));
    setTouchedState((prev) => ({
      ...prev,
      workflowId: true,
    }));
  }, [userCanChangeWorkflow, userIsAdmin]);

  const handleStartTimeDropdownChange = useCallback(
    (value: string | null) => {
      if (!value) {
        setFormState((prev) => ({ ...prev, startTime: null }));
        setTouchedState((prev) => ({
          ...prev,
          startTime: true,
        }));
        return;
      }

      const normalized = formatDate({
        date: value,
        format: ISO_8601,
        utcMode: false,
        shiftTz: siteTimeZone,
        tzKeepLocalTime: true,
      });

      setFormState((prev) => ({
        ...prev,
        startTime: normalized || null,
      }));
      setTouchedState((prev) => ({
        ...prev,
        startTime: true,
      }));
    },
    [siteTimeZone],
  );

  const handleSlugDropdownChange = useCallback((_event: unknown, value: string) => {
    setFormState((prev) => ({
      ...prev,
      slug: value ?? '',
    }));
    setTouchedState((prev) => ({
      ...prev,
      slug: true,
    }));
  }, []);

  const handleTitleDropdownChange = useCallback((_event: unknown, value: string) => {
    setFormState((prev) => ({
      ...prev,
      title: value ?? '',
    }));
    setTouchedState((prev) => ({
      ...prev,
      title: true,
    }));
  }, []);

  const handleStoryTemplateSelect = useCallback(
    (_event: unknown, value: string | string[]) => {
      const nextValue = Array.isArray(value) ? value[0] : value;
      setFormState((prev) => ({
        ...prev,
        storyTemplateId: nextValue || null,
      }));
      setTouchedState((prev) => ({
        ...prev,
        storyTemplateId: true,
      }));
    },
    [],
  );

  const handleStoryTemplateClear = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      storyTemplateId: null,
    }));
    setTouchedState((prev) => ({
      ...prev,
      storyTemplateId: true,
    }));
  }, []);

  const handleSubmit = () => {
    const payload: AssetQuickLaunchPrefill = {};
    const isDuplicate = mode === 'duplicate';

    const workflowValue = formState.workflowId ?? null;
    const normalizedWorkflow =
      typeof workflowValue === 'string' ? workflowValue.trim() : workflowValue;
    if (isDuplicate) {
      if (typeof normalizedWorkflow === 'string' && normalizedWorkflow.length > 0) {
        payload.workflowId = normalizedWorkflow;
      }
    } else if (touchedState.workflowId) {
      payload.workflowId = workflowValue;
    }

    const startTimeValue = formState.startTime ?? null;
    if (isDuplicate) {
      if (typeof startTimeValue === 'string' && startTimeValue.trim().length > 0) {
        payload.startTime = startTimeValue;
      } else {
        payload.startTime = null;
      }
    } else if (touchedState.startTime) {
      payload.startTime = startTimeValue;
    }

    const trimmedSlug = formState.slug.trim();
    if (isDuplicate) {
      payload.slug = trimmedSlug.length > 0 ? trimmedSlug : null;
    } else if (trimmedSlug.length > 0 && (mode === 'create' || touchedState.slug)) {
      payload.slug = trimmedSlug;
    }

    const trimmedTitle = formState.title.trim();
    const shouldSubmitTitle =
      trimmedTitle.length > 0 && (showTemplateNameField || touchedState.title);
    if (shouldSubmitTitle) {
      (payload as any).title = trimmedTitle;
    }

    // Include story template ID for create mode when a template is selected
    if (isCreateMode && formState.storyTemplateId) {
      payload.storyTemplateId = formState.storyTemplateId;
    }

    onSubmit(payload);
  };

  const handleClose = () => {
    onClose();
  };

  const handleModalClose = useCallback(
    (_event: PossibleCloseEvent, reason?: CustomCloseReasons) => {
      if (reason === 'cancelClose') {
        return;
      }

      onClose();
    },
    [onClose],
  );

  const workflowDisabled =
    workflowsQuery.isPending || (!userIsAdmin && !userCanChangeWorkflow);

  const workflowHelperText =
    !userIsAdmin && !userCanChangeWorkflow ? workflowPermissionHelper : workflowHelper;

  const descriptionId = 'asset-quick-launch-description';

  return (
    <DragModal
      id="asset-quick-launch-modal"
      data-testid="asset-quick-launch-modal"
      open={open}
      onClose={handleModalClose}
      isDraggable={true}
      isResizable={true}
      isFullscreenable={false}
      initialHeight={600}
      initialWidth={550}
      role="dialog"
      aria-modal="true"
      aria-labelledby="asset-quick-launch-title"
      aria-describedby={descriptionId}
      header={
        <Box
          id="asset-quick-launch-title"
          data-testid="asset-quick-launch-header"
        >
          <DragModalHeader
            icon={assetIcon}
            text={modalTitleText}
          />
        </Box>
      }
      body={
        <Box
          id={descriptionId}
          data-testid="asset-quick-launch-body"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 2,
            px: 1,
            py: 1,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {workflowsQuery.isError && (
            <Alert
              severity="error"
              variant="outlined"
              data-testid="asset-quick-launch-workflows-error"
            >
              {workflowsErrorMessage}
            </Alert>
          )}

          {showTemplateNameField && (
            <Stack
              spacing={0.5}
              data-testid="asset-quick-launch-title"
              sx={{ alignItems: 'flex-start', width: '100%' }}
            >
              <Label styles={{ marginTop: 0, marginBottom: 0 }}>{titleLabel}</Label>
              <Typography
                variant="caption"
                gutterBottom
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t(
                  mode === 'create-template'
                    ? 'blox.editorial.assetQuickLaunch.template.nameHelpNew'
                    : 'blox.editorial.assetQuickLaunch.template.nameHelp',
                )}
              </Typography>
              <TextField
                value={formState.title}
                onChange={(e) => handleTitleDropdownChange(null, e.target.value)}
                variant="outlined"
                fullWidth
                disabled={workflowsQuery.isPending}
                sx={{
                  mb: 0,
                  '& .MuiInputBase-input': {
                    fontSize: bloxValues.smallFontSizeRem,
                  },
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: bloxValues.smallFontSizeRem,
                  },
                }}
              />
            </Stack>
          )}

          {isCreateMode && (
            <Stack
              spacing={0.5}
              data-testid="asset-quick-launch-story-template"
              sx={{ alignItems: 'flex-start', width: '100%' }}
            >
              {storyTemplatesError ? (
                <Alert
                  severity="warning"
                  variant="outlined"
                  data-testid="asset-quick-launch-story-templates-error"
                  sx={{ width: '100%' }}
                >
                  {storyTemplatesErrorMessage}
                </Alert>
              ) : (
                <>
                  <Label styles={{ marginTop: 0, marginBottom: 0 }}>
                    {storyTemplateLabel}
                  </Label>
                  <Typography
                    variant="caption"
                    data-testid="asset-quick-launch-story-template-helper"
                    gutterBottom
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {storyTemplateHelper}
                  </Typography>
                  <SelectDropdownInput
                    group="asset-quick-launch-story-template"
                    value={formState.storyTemplateId ?? ''}
                    updateValue={handleStoryTemplateSelect}
                    clearSelection={handleStoryTemplateClear}
                    staticChoices={storyTemplateDropdownChoices}
                    queryOptions={{ enabled: false }}
                    infiniteScroll={false}
                    showFilter={storyTemplates.length > 5}
                    showFooter={false}
                    formControlVariant="outlined"
                    renderValueDisplay="chips"
                    popoverMatchesFormControlWidth={true}
                    disabled={storyTemplatesLoading}
                    showFormControlLabel={false}
                    formControlSx={getFormControlSxNoPadding}
                    selectSx={getSelectSxNoPadding}
                  />
                </>
              )}
            </Stack>
          )}

          <Stack
            spacing={0.5}
            data-testid="asset-quick-launch-workflow"
            sx={{ alignItems: 'flex-start', width: '100%' }}
          >
            <Label styles={{ marginTop: 0, marginBottom: 0 }}>{workflowLabel}</Label>
            <Typography
              variant="caption"
              data-testid="asset-quick-launch-workflow-helper"
              gutterBottom
              sx={{
                color: 'text.secondary',
              }}
            >
              {workflowHelperText}
            </Typography>
            <SelectDropdownInput
              group="asset-quick-launch"
              value={formState.workflowId ?? ''}
              updateValue={handleWorkflowSelect}
              clearSelection={workflowDisabled ? undefined : handleWorkflowClear}
              staticChoices={workflowDropdownChoices}
              showUnlabeledItems={false}
              queryOptions={{ enabled: false }}
              infiniteScroll={false}
              showFilter={false}
              showFooter={false}
              formControlVariant="outlined"
              renderValueDisplay="chips"
              popoverMatchesFormControlWidth
              disabled={workflowDisabled}
              showFormControlLabel={false}
              formControlSx={getFormControlSxNoPadding}
              selectSx={getSelectSxNoPadding}
            />
          </Stack>

          <Stack
            spacing={0.5}
            data-testid="asset-quick-launch-start-time"
            sx={{ alignItems: 'flex-start', width: '100%' }}
          >
            <Label styles={{ marginTop: 0, marginBottom: 0 }}>{startTimeLabel}</Label>
            <Typography
              variant="caption"
              data-testid="asset-quick-launch-start-time-helper"
              gutterBottom
              sx={{
                color: 'text.secondary',
              }}
            >
              {startTimeHelper}
            </Typography>
            <SingleDateDropdown
              value={formState.startTime}
              onChange={handleStartTimeDropdownChange}
              disabled={workflowsQuery.isPending}
              timezone={siteTimeZone}
              showFormControlLabel={false}
              formControlSx={getFormControlSxNoPadding}
              selectSx={getSelectSxNoPadding}
            />
          </Stack>

          <Stack
            spacing={0.5}
            data-testid="asset-quick-launch-slug"
            sx={{ alignItems: 'flex-start', width: '100%' }}
          >
            <Label styles={{ marginTop: 0, marginBottom: 0 }}>{slugLabel}</Label>
            <Typography
              variant="caption"
              data-testid="asset-quick-launch-slug-helper"
              gutterBottom
              sx={{
                color: 'text.secondary',
              }}
            >
              {slugHelper}
            </Typography>
            <TextDropdownInput
              value={formState.slug}
              updateValue={handleSlugDropdownChange}
              showFormControlLabel={false}
              formControlVariant="outlined"
              renderValueDisplay="chips"
              disabled={workflowsQuery.isPending}
              formControlSx={getFormControlSxNoPadding}
              selectSx={getSelectSxNoPadding}
            />
          </Stack>
        </Box>
      }
      footer={
        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            gap: 2,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            disableElevation
            data-testid="asset-quick-launch-cancel"
          >
            {cancelLabel}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={workflowsQuery.isPending}
            disableElevation
            data-testid="asset-quick-launch-submit"
          >
            {submitButtonLabel}
          </Button>
        </Stack>
      }
    />
  );
};
AssetQuickLaunchModal.displayName = 'AssetQuickLaunchModal';
